// frontend/src/app/shared/config/plan-features.ts
// ========================================================================
// Source unique de vérité côté frontend pour les fonctionnalités par plan.
// Miroir de: backend/src/config/planFeatures.js
// ========================================================================

export const FEATURES = {
  EVENTS_CREATE: 'events_create',
  RECOMMENDATIONS: 'recommendations',
  WEATHER_REALTIME: 'weather_realtime',
  EXPORT_PDF: 'export_pdf',
  ADVANCED_STATS: 'advanced_stats',
  AUTO_ALERTS: 'auto_alerts',
  PRIORITY_SUPPORT: 'priority_support',
  PREPARATION_CHECKLIST: 'preparation_checklist',
  ACTIVITY_ZONE: 'activity_zone',
  MULTI_STANDS: 'multi_stands',
  TEAM_MANAGEMENT: 'team_management',
  DEDICATED_SUPPORT: 'dedicated_support',
  CALENDAR_EXTENDED: 'calendar_extended',
} as const;

export type FeatureKey = (typeof FEATURES)[keyof typeof FEATURES];

export type PlanId = 'free' | 'starter' | 'pro' | 'business';

export const PLAN_LIMITS: Record<PlanId, { maxEventsPerMonth: number | null }> =
  {
    free: { maxEventsPerMonth: 3 },
    starter: { maxEventsPerMonth: 10 },
    pro: { maxEventsPerMonth: null },
    business: { maxEventsPerMonth: null },
  };

export const PLAN_FEATURES: Record<PlanId, Record<FeatureKey, boolean>> = {
  free: {
    [FEATURES.EVENTS_CREATE]: true,
    [FEATURES.RECOMMENDATIONS]: true,
    [FEATURES.WEATHER_REALTIME]: true,
    [FEATURES.EXPORT_PDF]: false,
    [FEATURES.ADVANCED_STATS]: false,
    [FEATURES.AUTO_ALERTS]: false,
    [FEATURES.PREPARATION_CHECKLIST]: false,
    [FEATURES.ACTIVITY_ZONE]: false,
    [FEATURES.PRIORITY_SUPPORT]: false,
    [FEATURES.MULTI_STANDS]: false,
    [FEATURES.TEAM_MANAGEMENT]: false,
    [FEATURES.DEDICATED_SUPPORT]: false,
    [FEATURES.CALENDAR_EXTENDED]: false,
  },
  starter: {
    [FEATURES.EVENTS_CREATE]: true,
    [FEATURES.RECOMMENDATIONS]: true,
    [FEATURES.WEATHER_REALTIME]: true,
    [FEATURES.EXPORT_PDF]: true,
    [FEATURES.ADVANCED_STATS]: false,
    [FEATURES.AUTO_ALERTS]: false,
    [FEATURES.PREPARATION_CHECKLIST]: false,
    [FEATURES.ACTIVITY_ZONE]: false,
    [FEATURES.PRIORITY_SUPPORT]: false,
    [FEATURES.MULTI_STANDS]: false,
    [FEATURES.TEAM_MANAGEMENT]: false,
    [FEATURES.DEDICATED_SUPPORT]: false,
    [FEATURES.CALENDAR_EXTENDED]: false,
  },
  pro: {
    [FEATURES.EVENTS_CREATE]: true,
    [FEATURES.RECOMMENDATIONS]: true,
    [FEATURES.WEATHER_REALTIME]: true,
    [FEATURES.EXPORT_PDF]: true,
    [FEATURES.ADVANCED_STATS]: true,
    [FEATURES.AUTO_ALERTS]: true,
    [FEATURES.PREPARATION_CHECKLIST]: true,
    [FEATURES.ACTIVITY_ZONE]: true,
    [FEATURES.PRIORITY_SUPPORT]: true,
    [FEATURES.MULTI_STANDS]: false,
    [FEATURES.TEAM_MANAGEMENT]: false,
    [FEATURES.DEDICATED_SUPPORT]: false,
    [FEATURES.CALENDAR_EXTENDED]: true,
  },
  business: {
    [FEATURES.EVENTS_CREATE]: true,
    [FEATURES.RECOMMENDATIONS]: true,
    [FEATURES.WEATHER_REALTIME]: true,
    [FEATURES.EXPORT_PDF]: true,
    [FEATURES.ADVANCED_STATS]: true,
    [FEATURES.AUTO_ALERTS]: true,
    [FEATURES.PREPARATION_CHECKLIST]: true,
    [FEATURES.ACTIVITY_ZONE]: true,
    [FEATURES.PRIORITY_SUPPORT]: true,
    [FEATURES.MULTI_STANDS]: true,
    [FEATURES.TEAM_MANAGEMENT]: true,
    [FEATURES.DEDICATED_SUPPORT]: true,
    [FEATURES.CALENDAR_EXTENDED]: true,
  },
};

/** Labels lisibles pour chaque fonctionnalité */
export const FEATURE_LABELS: Record<FeatureKey, string> = {
  [FEATURES.EVENTS_CREATE]: "Création d'événements",
  [FEATURES.RECOMMENDATIONS]: 'Recommandations IA',
  [FEATURES.WEATHER_REALTIME]: 'Météo temps réel',
  [FEATURES.EXPORT_PDF]: 'Export PDF',
  [FEATURES.ADVANCED_STATS]: 'Statistiques avancées',
  [FEATURES.AUTO_ALERTS]: 'Alertes automatiques',
  [FEATURES.PRIORITY_SUPPORT]: 'Support prioritaire',
  [FEATURES.PREPARATION_CHECKLIST]: 'Checklist de préparation (fixe)',
  [FEATURES.ACTIVITY_ZONE]: "Zone d'activité & Opportunités (itinérant)",
  [FEATURES.MULTI_STANDS]: 'Multi-stands',
  [FEATURES.TEAM_MANAGEMENT]: "Gestion d'équipe",
  [FEATURES.DEDICATED_SUPPORT]: 'Accompagnement personnalisé',
  [FEATURES.CALENDAR_EXTENDED]: 'Calendrier étendu (1 mois / 3 mois / 1 an)',
};

/** Ordre hiérarchique des plans */
export const PLAN_ORDER: PlanId[] = ['free', 'starter', 'pro', 'business'];

/** Labels lisibles pour chaque plan */
export const PLAN_LABELS: Record<PlanId, string> = {
  free: 'Gratuit',
  starter: 'Starter',
  pro: 'Pro',
  business: 'Business',
};

/**
 * Retourne le plan minimum requis pour une feature.
 */
export function getMinPlanForFeature(featureKey: FeatureKey): PlanId {
  for (const plan of PLAN_ORDER) {
    if (PLAN_FEATURES[plan]?.[featureKey]) return plan;
  }
  return 'business';
}
